/** paid-media API platform slug for budget writes (mirrors the UI's SupportedPlatform). */
export type SupportedPlatform = 'meta' | 'tiktok' | 'google';

export interface BudgetInputs {
  targetDRRoas: number;
  targetBrandRoas: number;
  targetDailyBudget: number;
  avgOrderValue: number;
}

export type EntityCategory = 'BRAND' | 'DR';

export type ConfidenceTier = 'LOW' | 'MEDIUM' | 'HIGH';

export const ANALYSIS_PERIOD_OPTIONS = [3, 7, 14, 30] as const;

export const DEFAULT_ANALYSIS_PERIOD = 7;

/** Shared option list for the per-row and global analysis period dropdowns */
export const ANALYSIS_PERIOD_SELECT_OPTIONS = ANALYSIS_PERIOD_OPTIONS.map((d) => ({
  value: d,
  label: `Last ${d}d`,
}));

/**
 * Recommendations at or below this daily budget ($) become a pause suggestion
 * instead of a tiny budget.
 */
export const PAUSE_BUDGET_THRESHOLD = 10;

/**
 * Client-side sanity bounds for a manually-entered daily budget ($). The
 * backend adapters enforce their own per-platform limits; these catch obvious
 * fat-finger errors (extra zero, empty, $0) before a write is ever attempted.
 */
export const MIN_DAILY_BUDGET = 1;
export const MAX_DAILY_BUDGET = 100_000;

/**
 * A budget change whose magnitude exceeds this (percent of current budget)
 * requires an explicit confirmation before it can be applied.
 */
export const LARGE_CHANGE_PCT = 200;

/** Warn when the newest complete data day is older than this many days. */
export const STALE_DATA_DAYS = 3;

/**
 * A row whose calendar-day average spend over its analysis window is below
 * this share of its platform daily budget is flagged as not spending to its
 * budget. The allocation counts rows at their budget, so the flag says where
 * that is likely to overstate what actually gets spent.
 */
export const UNDER_DELIVERY_RATIO = 0.9;

/** The account-local `YYYY-MM-DD` days (inclusive) a Google impression-share read covers. */
export interface ImpressionShareWindow {
  since: string;
  until: string;
}

/**
 * Google's impression-share sentinels: search_impression_share below 10% is
 * reported as 0.0999, a lost share above 90% as 0.9001. Neither is a
 * measurement; formatImpressionShare renders them as "<10%" / ">90%".
 */
export const IMPRESSION_SHARE_FLOOR = 0.0999;
export const IMPRESSION_SHARE_CAP = 0.9001;

/** Bidding-strategy context for Google campaign rows (smart scaling). */
export interface GoogleBiddingInfo {
  /** Google's tROAS fraction (4.5 = 450%); undefined for non-tROAS strategies. */
  targetRoas?: number;
  biddingStrategyType?: string;
  /** Google's own serving diagnosis: what limits this campaign right now. */
  constraint: 'target' | 'budget' | null;
  learning: boolean;
  /**
   * Portfolio strategies are excluded from UI tROAS moves — the target is
   * shared across campaigns (multi-campaign blast radius); use MCP with
   * portfolioOverride instead.
   */
  portfolio: boolean;
  /**
   * Search impression share over the trailing complete days (the live
   * Google read's window, account timezone), as fractions: the share of
   * eligible Search impressions the campaign won, and the shares it lost to
   * budget and to rank. Search and Shopping campaigns only — null elsewhere
   * or when nothing served; absent when the live read could not fetch them.
   * Diagnosis only: what limits delivery, not an input to the recommendation.
   */
  searchImpressionShare?: number | null;
  searchBudgetLostShare?: number | null;
  searchRankLostShare?: number | null;
  impressionShareWindow?: ImpressionShareWindow | null;
}

/** Why a row carries a "recent change" label. */
export type RecentChangeReason = 'budget' | 'bid-target' | 'enabled' | 'first-spend';

/**
 * A recent manual change — a person or agent set this row's budget or bid
 * target, or enabled it (read from the Audit table) — or the row's first
 * spend day, inside the company's recent change window (Financial Settings,
 * historically the "manual change hold"). The row is LABELED so the
 * operator can decide whether to include it, and approving a change to it
 * asks for an explicit acknowledgment. It is never pinned: checked, it
 * reallocates like any other row; unchecked, it stays at its current budget
 * like any other unchecked row.
 */
export interface RecentChange {
  reason: RecentChangeReason;
  /** ISO timestamp of an audited change, or the YYYY-MM-DD first spend day. */
  at: string;
  daysAgo: number;
  /** The Audit row that proves an audited change; null for first spend. */
  auditId: string | null;
  actor: string | null;
  /**
   * Budget changes: the daily budget before and after, in dollars, when the
   * audit recorded them — so a minor past shift reads as minor.
   */
  previousDailyBudget?: number;
  newDailyBudget?: number;
}

/**
 * How the Budget Recommender treats rows whose initiative type is
 * BRANDED_SEARCH (Financial Settings). `fixed` holds them at their current
 * budget, exactly like a pin; `optimize` reallocates them like any DR row.
 * Brand search is scored against the DR ROAS target, where its modeled ROAS
 * sits below target by construction, so `optimize` cuts it on every run.
 */
export const BRAND_SEARCH_POLICIES = ['fixed', 'optimize'] as const;
export type BrandSearchPolicy = (typeof BRAND_SEARCH_POLICIES)[number];
export const DEFAULT_BRAND_SEARCH_POLICY: BrandSearchPolicy = 'fixed';
/** The initiative type the brand-search policy applies to. */
export const BRAND_SEARCH_INITIATIVE_TYPE = 'BRANDED_SEARCH';

/**
 * The company step cap (Financial Settings `maxBudgetStepPct`): the most one
 * run may move a checked row, as a fraction of its current budget. Null = off.
 */
export const MAX_BUDGET_STEP_PCT_MIN = 0.1;
export const MAX_BUDGET_STEP_PCT_MAX = 0.3;
/** The per-row step cap override (`BudgetRecommenderRowSetting.stepCapPct`). */
export const ROW_STEP_CAP_PCT_MIN = 0.05;
export const ROW_STEP_CAP_PCT_MAX = 0.3;

/** What holds a row at its current budget: a saved pin, or the brand-search policy. */
export type RowLockSource = 'pin' | 'brand-policy';

/**
 * A row the recommender may not move this run. A locked row is always
 * treated as unchecked, whatever the selection: it keeps its current budget
 * (never a pause) and, when active, commits that budget to its category's
 * share before the checked rows are scaled. A manual pin wins over the
 * brand-search policy when both apply.
 */
export interface RowLock {
  source: RowLockSource;
  /** The pin's reason, or the policy's name for a brand-policy lock. */
  reason: string | null;
  /** Email of whoever set the pin; null for the policy. */
  pinnedBy: string | null;
  /** ISO timestamp the pin was set; null for the policy. */
  pinnedAt: string | null;
  /** ISO timestamp the pin lapses; null = until unpinned (always null for the policy). */
  expiresAt: string | null;
}

/** Delivery status reported by the platform (live API or Cube), normalized. */
export type PlatformStatus = 'ACTIVE' | 'INACTIVE';

/** Why a row sits outside the allocation pool. */
export type InactiveReason = 'paused' | 'no-spend';

/** A combined tROAS move applied alongside a budget approval. */
export interface TroasChange {
  fromTargetRoas: number;
  toTargetRoas: number;
  source: 'simulation' | 'heuristic';
}

export interface RecommendationRow {
  entityId: string;
  entityName: string;
  entityType: 'campaign' | 'adset';
  campaignId: string;
  initiativeType: string;
  /** null when initiative type is unassigned */
  category: EntityCategory | null;
  /** Display name: "Meta", "TikTok", "Pinterest", "Google Ads", "YouTube" */
  channel: string;
  /** paid-media API platform slug for budget writes; null for unregistered (read-only) channels. */
  platform: SupportedPlatform | null;
  /** true for channels we can apply budgets to via API (Meta, TikTok, Google incl. YouTube). */
  canApplyBudget: boolean;
  /** Present on Google campaign rows: bidding strategy + constraint diagnosis. */
  googleBidding?: GoogleBiddingInfo;
  /**
   * Recent manual changes and/or first spend inside the recent change
   * window, newest first. Absent or empty when there are none (or the window
   * is 0). A label, not a lock — see RecentChange.
   */
  recentChanges?: RecentChange[];
  /** Normalized delivery status; undefined when no source reports one. */
  platformStatus?: PlatformStatus;
  /**
   * In the allocation pool: not paused on the platform AND spend in the
   * analysis window. An unchecked active row commits its current budget to
   * the pool; an inactive row never does, and is left out of the review
   * unless the user checks it.
   */
  isActive: boolean;
  inactiveReason?: InactiveReason;
  /**
   * currentDailyBudget came from the platform (true) rather than falling
   * back to average daily spend (false, see normalizeCurrentBudget).
   */
  hasPlatformBudget: boolean;
  /**
   * Calendar-day average spend over the analysis window ÷ the platform
   * daily budget. Null without a platform budget. Below UNDER_DELIVERY_RATIO
   * the row is flagged as not spending to its budget.
   */
  deliveryRatio: number | null;
  /** Total spend over this row's analysis period */
  periodSpend: number;
  /** modeledRevenue / spend (0 if no spend) */
  drRoas: number;
  /** brandValue / spend (0 if no spend) */
  brandRoas: number;
  /**
   * Effective current daily budget. Falls back to avgDailySpend when the
   * platform reports no budget (Google/YouTube) or $0, so the Current Budget
   * column and % Change stay meaningful instead of reading $0 / 0%.
   */
  currentDailyBudget: number;
  /** Raw performance-based recommendation (never overwritten by scaling) */
  recommendedBudget: number;
  /** Raw performance-based recommendation before scaling (used as cap) */
  performanceBudget: number;
  /** periodSpend / daysWithSpend */
  avgDailySpend: number;
  /** Number of days in the analysis period with spend > 0 */
  daysWithSpend: number;
  /** Final budget after scale-down and caps — the actionable value */
  adjustedBudget: number;
  percentChange: number;
  editedBudget?: number;
  hasMissingData: boolean;
  /** Modeled revenue over the analysis period (for DR ROAS aggregation) */
  modeledRevenuePeriod: number;
  /** Modeled brand value over the analysis period (for Brand ROAS aggregation) */
  brandValuePeriod: number;
  /** true when recommended budget < $10/day — entity should be paused instead */
  shouldPause: boolean;
  /**
   * The pause comes from scaling to the pool, not from performance: the row
   * clears PAUSE_BUDGET_THRESHOLD at its raw recommendation (after the
   * caps) and drops below it once scaled. Set by allocateBudgets.
   */
  pausedByScaling?: boolean;
  /**
   * Set when the row may not move this run (an unexpired pin, or the
   * brand-search policy). Set by applyRowControls; allocateBudgets treats a
   * locked row as unchecked whatever the selection.
   */
  lock?: RowLock;
  /**
   * The step cap that applies to this row, as a fraction of its current
   * budget: the row's own override, else the company's. Absent = no cap.
   * Set by applyRowControls.
   */
  stepCapPct?: number;
  /**
   * allocateBudgets clamped this checked row to current × (1 ± stepCapPct).
   * `unclampedBudget` is what the lane scale and caps gave before the clamp.
   */
  stepCapped?: boolean;
  unclampedBudget?: number;
  /**
   * For campaign rows: true if some child ad sets with spend have no initiative
   * type of their own and the campaign has no campaign-level type for them to
   * inherit, so attribution leaves their spend untyped.
   */
  hasUncategorizedAdsets: boolean;
  /**
   * The campaign's full sibling ad set IDs (Meta/TikTok only). On a campaign
   * row, used to cascade a campaign-level initiative type change down to its ad
   * sets; on an ad set row, used so "apply to entire campaign" cascades to every
   * sibling. Undefined for Google/YouTube (which classify at campaign level).
   */
  childAdsetIds?: string[];
  /** Length of this row's analysis window in days (3 / 7 / 14 / 30) */
  analysisPeriodDays: number;
  /**
   * Inclusive end date (YYYY-MM-DD) of this row's analysis window. Equals the
   * page-level latestCompleteDate except for Google-platform (YouTube or
   * Google Ads channel) Demand Generation rows, whose window is anchored to
   * the latest day with non-zero brand value — YouTube Analytics data lands a
   * few days after Google Ads spend data, and a YouTube campaign can surface
   * under either channel. Derived from data on every load; never persisted.
   */
  dataThroughDate: string;
  /**
   * Estimated conversions over the analysis period, driving the confidence tier.
   * Demand Capture (DR) uses modeledRevenuePeriod / avgOrderValue; Demand
   * Generation (BRAND) uses brandValuePeriod / avgOrderValue. Null when AOV is
   * unset.
   */
  estimatedConversions: number | null;
  /** Confidence tier from estimated conversions — null when AOV is unset */
  confidence: ConfidenceTier | null;
  /**
   * Estimated conversions for every selectable analysis period
   * (ANALYSIS_PERIOD_OPTIONS), each over the candidate window ending at this
   * row's dataThroughDate and on the same basis as estimatedConversions, so
   * the current period's entry equals estimatedConversions. Powers
   * "auto-select analysis period by confidence". Every entry is null when AOV
   * is unset.
   */
  estimatedConversionsByPeriod: Record<number, number | null>;
  /**
   * Window-aggregated Cube measures this row's modeled revenue was derived from
   * (short measure names → summed values). Used by the initiative-type change
   * modal to project modeled revenue for a different type on the frontend.
   * Omitted for rows whose modeled revenue comes from an adset-summed override
   * (Meta/TikTok CBO campaigns), where no single measures dict reproduces it.
   */
  measures?: Record<string, number>;
}

export interface ApplyResult {
  entityId: string;
  entityName: string;
  channel: string;
  success: boolean;
  /** true when the entity was paused rather than having its budget updated */
  paused?: boolean;
  /** true when the row was left alone (reported, never written) */
  skipped?: boolean;
  error?: string;
  /** Combined smart-scale outcome: the tROAS half applied (budget half is `success`). */
  troasApplied?: boolean;
  /** Set when the budget applied but the tROAS change failed — partial success. */
  troasError?: string;
}
